import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';

import StrapiButton from '../../StrapiLink/StrapiButton';
import StrapiLink from '../../StrapiLink/StrapiLink';
import { CheckMarkCircle } from '../../Icons/CheckMarkCircle';
import { Dismiss } from '../../Icons/Dismiss';
import { LinkComponentFragment } from '../../../../graphql.schema';

import * as styles from './StrapiPricingCards.module.scss';

type StrapiPricingCardsProps = {
  pricing_card_title: string;
  pricing_card_description: string;
  pricing_cards_delivery_methods: {
    delivery_method: {
      bannerText?: string;
      pricingPlanHeading?: string;
      pricingPlanDescription?: string;
      price?: {
        label?: string;
        price: number;
        frequency?: string;
      };
      isRecommended?: boolean;
      features: {
        content: string;
        isFaded: boolean;
      }[];
      link?: LinkComponentFragment;
      footnote?: string;
    }
    secondary_link?: LinkComponentFragment;
  }[];
};

export const StrapiPricingCards = (props: StrapiPricingCardsProps) => {
  const [maxCardHeight, setMaxCardHeight] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const calculateMaxHeight = () => {
      setMaxCardHeight(0);
      
      let maxHeight = 0;
      cardRefs.current.forEach((card) => {
        if (card) {
          const height = card.offsetHeight;
          maxHeight = Math.max(maxHeight, height);


        }
      });
        setMaxCardHeight(maxHeight);
    };

    const handleResize = () => {
      calculateMaxHeight();
    };
    handleResize();
  }, [props.pricing_cards_delivery_methods]);
  
  const singleCard = props.pricing_cards_delivery_methods.length === 1;

  return (
    <div className={classnames(styles.root,  {
      [styles.singleCard]: singleCard,
    })}>
      <div className={classnames(styles.titleContainer, {
        [styles.singleCard]: singleCard,
      })}>
        <h2 className={classnames('title-xlarge', styles.title)}>{props.pricing_card_title}</h2>
        <p className={classnames('is-text-2-v2', styles.description)}>{props.pricing_card_description}</p>
      </div>

      <div className={classnames(styles.cardsContainer, {
        [styles.singleCard]: singleCard,
      })}>
        {props.pricing_cards_delivery_methods.length ?
          props.pricing_cards_delivery_methods.map((card, index) => {
            return (
              <div key={card.delivery_method.pricingPlanHeading} className={classnames(styles.card, {
                  [styles.highlighted]: card.delivery_method.isRecommended
                })}
              >
                {card.delivery_method.isRecommended && card.delivery_method.bannerText ?
                  <p className={classnames('text-small', styles.badge)}>{card.delivery_method.bannerText}</p>
                : null}
                <div className={styles.cardContentContainer}>
                  <div 
                    className={styles.cardInnerContainer}
                    ref={(el) => (cardRefs.current[index] = el)}
                    style={{ minHeight: maxCardHeight > 0 ? `${maxCardHeight}px` : 'auto' }}
                  >
                    <div>
                      <h3 className="title-large">{card.delivery_method.pricingPlanHeading}</h3>
                      <p className="is-text-2-v2">{card.delivery_method.pricingPlanDescription}</p>
                    </div>

                    <div>
                      {card.delivery_method.price && card.delivery_method.price.label ? (
                        <p className={classnames('is-text-3', styles.cardPriceTitle)}>
                          {card.delivery_method.price.label}
                        </p>
                      ) : null}

                      {card.delivery_method.price ?
                        <>
                          <p className={styles.cardPrice}>
                            <span className="title is-title-2-v2">
                              {card.delivery_method.price.price}
                            </span>
                            {card.delivery_method.price.frequency ?
                              <span className={classnames('is-text-3', styles.cardPriceLabel)}>
                                {card.delivery_method.price.frequency}
                              </span>
                            : null}
                          </p>
                        </>
                      : null}
                    </div>
                  </div>

                  {card.delivery_method.link ?
                    <StrapiButton
                      type={singleCard || (!singleCard && card.delivery_method.isRecommended) ? "primary" : "secondary"}
                      className={classnames(styles.primaryCta, {
                        [styles.secondaryCtaStyle]: !singleCard && !card.delivery_method.isRecommended,
                      })}
                      {...card.delivery_method.link}
                    />
                  : null}
                </div>

                <div className={styles.bulletPointsContainer}>
                  <ul>
                    {card.delivery_method.features.map((feature) => {
                      return (
                        <li key={feature.content} className={classnames(styles.bulletPointElement, {[styles.inactive]: feature.isFaded })}>
                          <div className={styles.checkMarkIconContainer}>
                            {feature.isFaded ? <Dismiss /> : <CheckMarkCircle />}
                          </div>
                          {feature.content}
                        </li>
                      )
                    })}
                  </ul>

                  {card.secondary_link ? (
                    <StrapiLink
                      className={styles.secondaryCta}
                      {...card.secondary_link}
                    />
                  ) : null}

                  {card.delivery_method.footnote ? (
                    <p className={classnames('is-note', styles.footer)}>
                      {card.delivery_method.footnote}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          }) 
          : null}
      </div>
    </div>
  );
};
